import * as React from "react";
import { Modal, ModalBody } from "./Modal";
import { Button, ButtonVariant } from "./Button";
import { Banner } from "./States";
import { Textarea } from "./Form";
import { IconInput } from "./Icon";

/**
 * The confirmation step for destructive or irreversible actions.
 *
 * Replaces two things: ScadsysIdentity's per-page hand-rolled confirm modals, and this app's
 * `window.confirm` / `window.prompt` calls (MarkEntryGrid used a prompt to collect a moderation
 * rejection comment, which cannot be styled, cannot be cancelled cleanly, and blocks the page).
 *
 * `requireComment` covers the mark-workflow Reject case: the server rejects a blank comment, so the
 * confirm button stays disabled until one is typed rather than letting the user discover that as a
 * 400.
 */

export interface ConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (comment: string) => void | Promise<void>;
    title: string;
    icon?: IconInput;
    /** What will happen. Be specific: name the thing and say whether it can be undone. */
    body: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmVariant?: ButtonVariant;
    busy?: boolean;
    /** Shows the confirmation but refuses it, e.g. when the body explains a missing permission. */
    confirmDisabled?: boolean;
    error?: React.ReactNode;
    requireComment?: boolean;
    commentLabel?: string;
    commentPlaceholder?: string;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    icon = ["fad", "triangle-exclamation"],
    body,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    confirmVariant = "danger",
    busy = false,
    confirmDisabled = false,
    error,
    requireComment = false,
    commentLabel = "Comment",
    commentPlaceholder = "",
}) => {
    const [comment, setComment] = React.useState("");

    // Reset on open so a comment typed for a previous target never leaks into the next one.
    React.useEffect(() => {
        if (isOpen) setComment("");
    }, [isOpen]);

    const blocked = busy || confirmDisabled || (requireComment && !comment.trim());

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            icon={icon}
            iconColor={confirmVariant === "danger" ? "text-danger" : "text-primary"}
            size="sm"
            footer={
                <>
                    <Button variant="outline-secondary" onClick={onClose} disabled={busy}>
                        {cancelLabel}
                    </Button>
                    <Button variant={confirmVariant} onClick={() => onConfirm(comment.trim())} loading={busy} disabled={blocked}>
                        {confirmLabel}
                    </Button>
                </>
            }
        >
            <ModalBody>
                <div className="space-y-3">
                    <div className="text-sm text-text">{body}</div>

                    {requireComment && (
                        <div>
                            <label className="mb-1 block text-sm font-medium text-text">
                                {commentLabel}
                                <span className="ml-1 text-danger">*</span>
                            </label>
                            <Textarea
                                value={comment}
                                placeholder={commentPlaceholder}
                                onChange={(e) => setComment(e.target.value)}
                                autoFocus
                            />
                        </div>
                    )}

                    {error && <Banner tone="danger">{error}</Banner>}
                </div>
            </ModalBody>
        </Modal>
    );
};

export default ConfirmModal;
